import * as THREE from "three";

// Foliage push (spike 002, decision 0085): plants that stay walk-through bend away from the characters passing
// through them, then settle back as they leave. A vertex-stage mechanism on the low-poly surface: each vertex with a
// `sway` weight (0 rigid … 1 full; geometry without the attribute reads 0) moves horizontally away from each of a few
// pushers (a character's feet and a radius, standing as an upright capsule `height` tall) with a smoothstep falloff.
// The pushes are summed and capped at one full push, pressed down a little (a bend lowers the top; a plant right over
// the pusher's axis, which has no sideways way out, is pressed down) and wobble while pushed.
// No state per plant: it springs back as the pusher's distance grows. The shadow pass draws the same displacement
// (`swayDepthMaterial`, the surface's depth twin). The game owns every value (`FoliagePush`) and feeds the pushers each
// frame (`setPushers`); the package owns the mechanism.
//
// Every square root and division in the GLSL is guarded (a NaN here once blanked half a screen through bloom on a
// D3D11 GPU): no normalize, distances from sqrt(max(dot, 0)), directions divided by max(length, 1e-3), radii > 0.

/** The per-vertex sway weight's attribute name (one float: 0 rigid … 1 sways fully). */
export const SWAY_ATTRIBUTE = "sway";

/** The most pushers the surface reads at once (the hero and a few others nearby). */
export const MAX_PUSHERS = 4;

/** The game's values for the push. */
export interface FoliagePush {
  /** A pusher's reach (m) from its upright axis, when the pusher gives none. */
  radius: number;
  /** A pusher's height (m) above its feet: plants above or below the capsule are not pushed. */
  height: number;
  /** How far (m) a fully swaying vertex moves at a full push. 0: off. */
  strength: number;
  /** How far a pushed vertex is pressed down, a fraction of `strength` at a full push (a bend lowers the top, and a
   *  plant right over the pusher has no sideways way out), 0 to 1. */
  dip: number;
  /** The push's wobble while pushed, 0 to 1 (a fraction of the push). */
  wobble: number;
  /** The wobble's rate (radians a second). */
  rate: number;
}

/** One pusher: a character's feet (world, m) and optionally its own reach. */
export interface Pusher {
  x: number;
  y: number;
  z: number;
  radius?: number;
}

/** The push's uniforms (part of the low-poly look's `LowPolyUniforms`). */
export interface PushUniforms {
  /** Pushers: feet xyz, reach w (0: unused slot). */
  uLpPush: THREE.IUniform<THREE.Vector4[]>;
  /** strength, dip, wobble, rate. */
  uLpPushK: THREE.IUniform<THREE.Vector4>;
  /** time (s), height (m), default reach (m), unused. */
  uLpPushT: THREE.IUniform<THREE.Vector4>;
}

/** Planted defects beside what they break: `ignoreWeight` pushes every vertex fully (terrain, rocks, buildings
 *  bend); `noFalloff` pushes at full strength anywhere inside the reach (a hard edge, a jump when a pusher enters). */
export const SWAY_FAULTS = { ignoreWeight: false, noFalloff: false };

/** The push's uniforms, off (no pushers, no strength). */
export function pushUniforms(): PushUniforms {
  return {
    uLpPush: { value: Array.from({ length: MAX_PUSHERS }, () => new THREE.Vector4(0, 0, 0, 0)) },
    uLpPushK: { value: new THREE.Vector4(0, 0, 0, 0) },
    uLpPushT: { value: new THREE.Vector4(0, 0, 0, 0) },
  };
}

const finite = (v: number, d = 0): number => (Number.isFinite(v) ? v : d);
const clamp = (v: number, a: number, b: number): number => Math.min(b, Math.max(a, v));

/** Write the game's push values (sanitised: non-finite values are 0, the reach and height at least 1 cm). */
export function setFoliagePush(u: PushUniforms, p: FoliagePush): void {
  u.uLpPushK.value.set(Math.max(0, finite(p.strength)), clamp(finite(p.dip), 0, 1), clamp(finite(p.wobble), 0, 1), finite(p.rate));
  u.uLpPushT.value.y = Math.max(0.01, finite(p.height, 0.01));
  u.uLpPushT.value.z = Math.max(0.01, finite(p.radius, 0.01));
}

/** Feed this frame's pushers (at most `MAX_PUSHERS`; the rest are dropped) and the time the wobble runs on (the
 *  game's clock, so a paused or stepped frame is the same frame). A pusher with a non-finite position is skipped. */
export function setPushers(u: PushUniforms, pushers: readonly Pusher[], time: number): void {
  const slots = u.uLpPush.value;
  let n = 0;
  for (const p of pushers) {
    if (n >= MAX_PUSHERS) break;
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z)) continue;
    const r = p.radius ?? u.uLpPushT.value.z;
    slots[n++]!.set(p.x, p.y, p.z, Number.isFinite(r) && r > 0 ? Math.max(0.01, r) : 0);
  }
  for (; n < MAX_PUSHERS; n++) slots[n]!.set(0, 0, 0, 0);
  u.uLpPushT.value.x = finite(time);
}

/**
 * The displacement the shader gives a vertex at world `p` with sway weight `weight` (a CPU mirror of the GLSL, for
 * tests and for anything that must agree with what is drawn). Returns a world offset (m).
 */
export function pushOffset(p: { x: number; y: number; z: number }, weight: number, pushers: readonly Pusher[], push: FoliagePush, time: number): { x: number; y: number; z: number } {
  const u = pushUniforms();
  setFoliagePush(u, push);
  setPushers(u, pushers, time);
  const w = SWAY_FAULTS.ignoreWeight ? 1 : weight;
  if (!SWAY_FAULTS.ignoreWeight && !(w > 0)) return { x: 0, y: 0, z: 0 };
  const K = u.uLpPushK.value;
  const T = u.uLpPushT.value;
  let sx = 0;
  let sz = 0;
  let sf = 0;
  for (const q of u.uLpPush.value) {
    if (!(q.w > 0)) continue;
    const dx = p.x - q.x;
    const dz = p.z - q.z;
    const dy = Math.max(q.y - p.y, p.y - q.y - T.y, 0);
    const l = Math.sqrt(Math.max(dx * dx + dz * dz, 0));
    const s = clamp(1 - Math.sqrt(Math.max(l * l + dy * dy, 0)) / q.w, 0, 1);
    const f = SWAY_FAULTS.noFalloff ? (s > 0 ? 1 : 0) : s * s * (3 - 2 * s);
    const k = f / Math.max(l, 1e-3);
    sx += dx * k;
    sz += dz * k;
    sf += f;
  }
  const m = Math.sqrt(Math.max(sx * sx + sz * sz, 0));
  const cap = 1 / Math.max(m, 1);
  const wob = 1 + K.z * Math.sin(T.x * K.w + p.x * 1.7 + p.z * 2.3);
  const g = K.x * w * wob;
  return { x: sx * cap * g, y: -K.y * Math.min(sf, 1) * g, z: sz * cap * g };
}

/** The shader text the push adds to a vertex shader, read at each compile (the faults change it). `decl` goes before
 *  main; `body` goes just before `#include <project_vertex>` and moves `transformed`, so everything after it (the
 *  world position, the shadow coordinates, the flat normals) sees the bent plant. */
export function swayChunks(): { decl: string; body: string } {
  const weight = SWAY_FAULTS.ignoreWeight ? "1.0" : SWAY_ATTRIBUTE;
  const falloff = SWAY_FAULTS.noFalloff ? "step(1e-6, s)" : "s * s * (3.0 - 2.0 * s)";
  return {
    decl: /* glsl */ `attribute float ${SWAY_ATTRIBUTE};
uniform vec4 uLpPush[${MAX_PUSHERS}];
uniform vec4 uLpPushK;
uniform vec4 uLpPushT;
vec3 lpPushOffset(vec3 wp, float w) {
  vec2 sum = vec2(0.0);
  float sf = 0.0;
  for (int i = 0; i < ${MAX_PUSHERS}; i++) {
    vec4 p = uLpPush[i];
    if (p.w > 0.0) {
      vec2 d = wp.xz - p.xz;
      float dy = max(max(p.y - wp.y, wp.y - p.y - uLpPushT.y), 0.0);
      float l = sqrt(max(dot(d, d), 0.0));
      float s = clamp(1.0 - sqrt(max(l * l + dy * dy, 0.0)) / p.w, 0.0, 1.0);
      float f = ${falloff};
      sum += d * (f / max(l, 1e-3));
      sf += f;
    }
  }
  sum *= 1.0 / max(sqrt(max(dot(sum, sum), 0.0)), 1.0);
  float wob = 1.0 + uLpPushK.z * sin(uLpPushT.x * uLpPushK.w + wp.x * 1.7 + wp.z * 2.3);
  float g = uLpPushK.x * w * wob;
  return vec3(sum.x * g, -uLpPushK.y * min(sf, 1.0) * g, sum.y * g);
}
`,
    body: /* glsl */ `
  {
    float lpSw = ${weight};
    if (lpSw > 0.0 && uLpPushK.x > 0.0) {
      mat4 lpM = modelMatrix;
      #ifdef USE_INSTANCING
        lpM = lpM * instanceMatrix;
      #endif
      #ifdef USE_BATCHING
        lpM = lpM * batchingMatrix;
      #endif
      vec3 lpOff = lpPushOffset((lpM * vec4(transformed, 1.0)).xyz, lpSw);
      mat3 lpR = mat3(lpM);
      // the world offset back into the geometry's frame (identity for the world's chunks); a degenerate matrix is skipped
      if (abs(determinant(lpR)) > 1e-12) transformed += inverse(lpR) * lpOff;
    }
  }
`,
  };
}

/** A cache-key suffix that changes when a fault changes the push's shader text. */
export const swayKey = (): string => (SWAY_FAULTS.ignoreWeight ? "-w" : "") + (SWAY_FAULTS.noFalloff ? "-f" : "");

/** Patch a vertex shader (three's, with `#include <project_vertex>`) with the push. Throws if the chunk is missing. */
export function patchSwayShader(shader: { vertexShader: string; uniforms: Record<string, THREE.IUniform> }, u: PushUniforms): void {
  if (!shader.vertexShader.includes("#include <project_vertex>")) throw new Error("@rpg/look: sway patch: vertex shader has no #include <project_vertex>");
  Object.assign(shader.uniforms, { uLpPush: u.uLpPush, uLpPushK: u.uLpPushK, uLpPushT: u.uLpPushT });
  const c = swayChunks();
  shader.vertexShader = c.decl + shader.vertexShader.replace("#include <project_vertex>", c.body + "#include <project_vertex>");
}

/** Give a material the zero default for the sway attribute (three writes it when a geometry has none). */
export function defaultSway(m: THREE.Material): void {
  const d = m as unknown as { defaultAttributeValues?: Record<string, number[]> };
  d.defaultAttributeValues = { ...(d.defaultAttributeValues ?? {}), [SWAY_ATTRIBUTE]: [0] };
}

/** The surface's depth twin for the shadow pass: three's depth material with the same push, so a bent plant's
 *  shadow bends with it. Set it as `customDepthMaterial` on meshes whose geometry sways (the look's `track` does). */
export function swayDepthMaterial(u: PushUniforms): THREE.MeshDepthMaterial {
  const m = new THREE.MeshDepthMaterial();
  m.name = "lowpoly-surface-depth";
  m.onBeforeCompile = (s) => patchSwayShader(s, u);
  m.customProgramCacheKey = () => "lowpoly-surface-depth-1" + swayKey();
  defaultSway(m);
  return m;
}

/** Give a geometry a zero `sway` attribute if it has none. */
export function ensureSway(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  if (!geo.getAttribute(SWAY_ATTRIBUTE)) geo.setAttribute(SWAY_ATTRIBUTE, new THREE.BufferAttribute(new Float32Array(geo.getAttribute("position").count), 1));
  return geo;
}

/** Whether a geometry has any vertex that sways. */
export function sways(geo: THREE.BufferGeometry): boolean {
  const a = geo.getAttribute(SWAY_ATTRIBUTE);
  if (!a) return false;
  for (let i = 0; i < a.count; i++) if (a.getX(i) > 0) return true;
  return false;
}
